import { Dispatch, SetStateAction, useMemo } from "react";
import {
  Radio,
  RadioSize,
  Input,
  Badge,
  BadgeVariant,
  BadgeSize,
  TruncateWithTooltip,
  Show
} from "shared/components";
import { cx, formatToCurrency } from "shared/utils";
import { CriteriaRentType, Money, Unit } from "shared/types";
import { useAuthContext } from "providers/Auth";
import { Template } from "types/landlord/verificationTemplate";

interface SetRentAmountOptionsProps {
  unit: Unit & { verification_template?: Template };
  manualRent: RentAmountData | undefined;
  setManualRent: Dispatch<SetStateAction<RentAmountData | undefined>>;
  useListedRent: boolean;
  setUseListedRent: (useListedRent: boolean) => void;
  selectedTemplate: Template | undefined;
}

export interface RentAmountData {
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

export const SetRentAmountOptions = ({
  unit,
  manualRent,
  setManualRent,
  useListedRent,
  setUseListedRent,
  selectedTemplate
}: SetRentAmountOptionsProps) => {
  const { permissions, pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  const handleManualRentChange = (
    field: keyof RentAmountData,
    value: string | undefined
  ) => {
    if (value == "") {
      setManualRent((prev) => ({
        ...prev,
        [field]: undefined
      }));
      return;
    }
    const cents = value ? parseFloat(value) * 100 : 0;
    setManualRent((prev) => ({
      ...prev,
      [field]: { cents, currency: "USD" }
    }));
  };

  const criteriaRentType = useMemo(() => {
    return (
      selectedTemplate?.criteria_rent_type ||
      unit.verification_template?.criteria_rent_type ||
      CriteriaRentType.Both
    );
  }, [selectedTemplate, unit.verification_template?.criteria_rent_type]);

  const totalRentCents = useMemo(() => {
    if (homeSiteRentEnabled) {
      if (criteriaRentType === CriteriaRentType.Site)
        return unit.site_rent_amount?.cents || 0;
      if (criteriaRentType === CriteriaRentType.Home)
        return unit.home_rent_amount?.cents || 0;
      return (
        (unit.home_rent_amount?.cents || 0) +
        (unit.site_rent_amount?.cents || 0)
      );
    }
    return unit.monthly_rent_amount?.cents || 0;
  }, [unit, homeSiteRentEnabled, criteriaRentType]);

  return (
    <div className={cx("text-content-700", homeSiteRentEnabled && "space-y-3")}>
      <div className="w-full flex items-center">
        <Radio
          id="rent-option-listed"
          checked={useListedRent}
          disabled={!permissions.canEditApplicationRent}
          onChange={() => {
            setUseListedRent(true);
            setManualRent(undefined);
          }}
          label="Use listed rent"
          name="rent-option"
          className="gap-3"
          size={RadioSize.LG}
        />
        {!homeSiteRentEnabled ? (
          <Badge
            className="ml-4"
            variant={BadgeVariant.PRIMARY}
            size={BadgeSize.BASE}
          >
            {formatToCurrency({
              cents: unit.monthly_rent_amount?.cents || 0,
              currency: "USD"
            })}
          </Badge>
        ) : (
          <div className="flex-1 flex justify-end items-center gap-4 text-sm text-content-300">
            <Show if={criteriaRentType !== CriteriaRentType.Site}>
              <div>
                <span className="block">Home rent</span>
                <span className="block font-medium">
                  {formatToCurrency({
                    cents: unit.home_rent_amount?.cents || 0,
                    currency: "USD"
                  })}
                </span>
              </div>
            </Show>
            <Show if={criteriaRentType === CriteriaRentType.Both}>
              <span className="font-medium text-content-1000">+</span>
            </Show>
            <Show if={criteriaRentType !== CriteriaRentType.Home}>
              <div>
                <span className="block">Site rent</span>
                <span className="block font-medium">
                  {formatToCurrency({
                    cents: unit.site_rent_amount?.cents || 0,
                    currency: "USD"
                  })}
                </span>
              </div>
            </Show>
            <span className="font-medium text-content-1000">=</span>
            <div>
              <span className="block">Total rent</span>
              <span className="block font-medium text-content-1000 text-right">
                {formatToCurrency({
                  cents: totalRentCents,
                  currency: "USD"
                })}
              </span>
            </div>
          </div>
        )}
      </div>
      <div
        className={cx(
          "flex",
          homeSiteRentEnabled
            ? "flex-col gap-5"
            : "justify-between h-12 items-center"
        )}
      >
        <Radio
          id="rent-option-manual"
          checked={!useListedRent}
          disabled={!permissions.canEditApplicationRent}
          onChange={() => {
            setUseListedRent(false);
            setManualRent(undefined);
          }}
          label="Set rent amount for application"
          name="rent-option"
          className="gap-3"
          size={RadioSize.LG}
        />
        {!useListedRent ? (
          <div className="space-y-4 text-content-1000">
            <div className="flex items-center gap-4 text-sm">
              {homeSiteRentEnabled ? (
                <>
                  <Show if={criteriaRentType !== CriteriaRentType.Site}>
                    <div className="flex-1">
                      <Input
                        id="home-rent"
                        label="Home rent"
                        type="money"
                        className="max-w-[267px]"
                        placeholder={(
                          (unit.home_rent_amount?.cents || 0) / 100
                        ).toString()}
                        value={
                          manualRent?.home_rent_amount?.cents !== undefined
                            ? (
                                manualRent.home_rent_amount.cents / 100
                              ).toString()
                            : ""
                        }
                        onChange={(e) =>
                          handleManualRentChange(
                            "home_rent_amount",
                            e.target.value
                          )
                        }
                        min={0}
                        max={99999}
                        required
                        hideErrorMessage
                      />
                    </div>
                  </Show>
                  <Show if={criteriaRentType === CriteriaRentType.Both}>
                    <span className="text-content-1000 font-medium mt-2">
                      +
                    </span>
                  </Show>
                  <Show if={criteriaRentType !== CriteriaRentType.Home}>
                    <div className="flex-1">
                      <Input
                        id="site-rent"
                        label="Site rent"
                        type="money"
                        className="max-w-[267px]"
                        placeholder={(
                          (unit.site_rent_amount?.cents || 0) / 100
                        ).toString()}
                        value={
                          manualRent?.site_rent_amount?.cents !== undefined
                            ? (
                                manualRent.site_rent_amount.cents / 100
                              ).toString()
                            : ""
                        }
                        onChange={(e) =>
                          handleManualRentChange(
                            "site_rent_amount",
                            e.target.value
                          )
                        }
                        min={0}
                        max={99999}
                        required
                        hideErrorMessage
                      />
                    </div>
                  </Show>
                  <span className="text-content-1000 font-medium mt-2">=</span>
                  <div className="flex flex-col justify-center gap-2 text-right">
                    <span className="text-content-400 min-w-[53px]">
                      Total rent
                    </span>
                    <span className="text-right font-medium">
                      <TruncateWithTooltip
                        content={formatToCurrency({
                          cents:
                            (manualRent?.site_rent_amount?.cents || 0) +
                            (manualRent?.home_rent_amount?.cents || 0),
                          currency: "USD"
                        })}
                        maxWidth="100px"
                        tooltipClassName="z-[9999]"
                      />
                    </span>
                  </div>
                </>
              ) : (
                <Input
                  id="application-rent"
                  type="money"
                  placeholder={(
                    (unit.monthly_rent_amount?.cents || 0) / 100
                  ).toString()}
                  value={
                    manualRent?.rent_amount?.cents !== undefined
                      ? (manualRent.rent_amount.cents / 100).toString()
                      : ""
                  }
                  onChange={(e) =>
                    handleManualRentChange("rent_amount", e.target.value)
                  }
                  min={0}
                  max={99999}
                  className="self-end !w-[126px]"
                  hideErrorMessage
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
